import Cookies from 'universal-cookie';
import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { ProductIdPrice } from '~/Api/ProductPrice';
import config from '~/config';
import { PreferenceCookieStorage } from '~/Preferences';
import { Product, ProductPrice } from '~/Product';

import { Action, ActionType } from './Action';

export interface State {
	groupId: string | null;
	byCurrencyProductId: { [key: string]: ProductIdPrice[] };
	request: {
		promise: Promise<any>;
		productIds: string[];
		currency: string | null;
	} | null;
	priceTypePreference: PreferenceCookieStorage;
	hideTooltipPreference: PreferenceCookieStorage;
}

function createPriceTypePreferenceStorage(): PreferenceCookieStorage {
	return new PreferenceCookieStorage(new Cookies(), 'price-type', {
		path: '/',
		httpOnly: false,
	});
}

function createHideTooltipPreferenceStorage(): PreferenceCookieStorage {
	const expires = new Date();
	expires.setDate(expires.getDate() + 30);

	return new PreferenceCookieStorage(new Cookies(), 'price-type-hide-tooltip', {
		path: '/',
		httpOnly: false,
		expires,
	});
}

function createState(
	priceTypePreference: PreferenceCookieStorage = createPriceTypePreferenceStorage(),
	hideTooltipPreference: PreferenceCookieStorage = createHideTooltipPreferenceStorage()
): State {
	return {
		groupId: null,
		byCurrencyProductId: {},
		request: null,
		priceTypePreference,
		hideTooltipPreference,
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				groupId: action.groupId,
				request: {
					promise: action.request,
					productIds: action.productIds,
					currency: action.currency,
				},
			};

		case ActionType.receive: {
			if (state.request?.promise !== action.request) {
				return state;
			}
			if (action.items === null) {
				return { ...state, request: null };
			}

			const byCurrencyProductId = action.items.reduce(
				(result, item) => {
					const key = `${action.currency ?? ''}-${item.productId}`;
					if (key in result) {
						result[key].push(item);
					} else {
						result[key] = [item];
					}

					return result;
				},
				{ ...state.byCurrencyProductId }
			);

			return {
				...state,
				request: null,
				byCurrencyProductId,
			};
		}

		case ActionType.pruneAll:
			return createState(state.priceTypePreference);

		case ActionType.updateHideTooltipPreference:
			return {
				...state,
				hideTooltipPreference: createHideTooltipPreferenceStorage(),
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectProductPrice(
	state: State,
	product: Product,
	currency: string | null,
	priceType: string | null,
	filterCalculatedPrice: boolean,
	groupId: string | null = null,
	defaultPriceCurrency: string = config.product.defaultPriceCurrency
): ProductPrice | null {
	if (state.groupId !== groupId && currency !== defaultPriceCurrency) {
		return null;
	}

	const key = `${currency ?? ''}-${product.id}`;
	const prices = currency === defaultPriceCurrency ? mapProductPrices(product) : state.byCurrencyProductId[key];
	if (currency !== defaultPriceCurrency && !(key in state.byCurrencyProductId)) {
		return null;
	}

	const filteredPrices =
		filterCalculatedPrice || priceType === null
			? prices.filter((price) => !(price.productPrice.origin?.isComputed ?? false))
			: prices;

	if (priceType !== null) {
		return filteredPrices.find((price) => price.productPrice.type === priceType)?.productPrice ?? null;
	}

	filteredPrices.sort((a, b) => a.productPrice.priority - b.productPrice.priority);
	return filteredPrices[0]?.productPrice ?? null;
}

function mapProductPrices(product: Product): ProductIdPrice[] {
	return product.prices.map((price) => new ProductIdPrice(product.id, price));
}

export function selectProductPrices(state: State, productId: string, currency: string | null): ProductPrice[] | null {
	const key = `${currency ?? ''}-${productId}`;
	if (!(key in state.byCurrencyProductId)) {
		return null;
	}
	return state.byCurrencyProductId[key].map((price) => price.productPrice);
}
